// Layout principal pour l'ecosysteme Abracada Bati
// Le Centre Commercial du Batiment
// Header global unifie

import { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Home, Users, Search, ShoppingBag, Briefcase, Building2,
  Bell, MessageCircle, PlusCircle, User, Menu, X,
  Play, Grid3X3, Heart, Settings, LogOut, Store, Building, MapPin,
  Compass, Newspaper, Map, Package, HelpCircle, Star, Bookmark,
  UserPlus, Shield, ChevronRight, Camera, Calendar
} from 'lucide-react';
import { Button } from '../ui/button';

import { API_URL } from '../../config/api';

// Navigation Abracada Bati (header principal) - Specialise Batiment
const ABRACADA_SECTIONS = [
  { id: 'artisans', label: 'Artisans', icon: Store, path: '/artisans' },
  { id: 'annonces', label: 'Annonces', icon: ShoppingBag, path: '/marketplace' },
  { id: 'communaute', label: 'Communaute', icon: Users, path: '/reseau' },
  { id: 'travaux', label: 'Travaux', icon: Briefcase, path: '/artisans?type=travaux' },
  { id: 'depannage', label: 'Depannage', icon: Building, path: '/artisans?type=depannage' },
];

// Navigation items (sous-navigation)
const NAV_ITEMS = [
  { id: 'home', label: 'Accueil', icon: Home, path: '/reseau' },
  { id: 'discover', label: 'Découvrir', icon: Search, path: '/reseau/decouvrir' },
  { id: 'reels', label: 'Reels', icon: Play, path: '/reseau/reels' },
  { id: 'artisans', label: 'Artisans', icon: Briefcase, path: '/artisans' },
  { id: 'marketplace', label: 'Marketplace', icon: ShoppingBag, path: '/marketplace' },
];

export default function EcosystemLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showSocialMenu, setShowSocialMenu] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    // Load user from localStorage
    const token = localStorage.getItem('ecosystem_token');
    const storedProfile = localStorage.getItem('ecosystem_profile');
    
    if (token && storedProfile) {
      setProfile(JSON.parse(storedProfile));
      fetchNotifications(token);
    }
  }, []);

  const fetchNotifications = async (token) => {
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data);
        setUnreadCount(data.filter(n => !n.is_read).length);
      }
    } catch (e) {
      console.error('Error fetching notifications:', e);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('ecosystem_token');
    localStorage.removeItem('ecosystem_profile');
    setUser(null);
    setProfile(null);
    navigate('/reseau');
  };

  const isActive = (path) => {
    if (path === '/reseau' && location.pathname === '/reseau') return true;
    if (path !== '/reseau' && location.pathname.startsWith(path)) return true;
    return false;
  };

  // Determiner la section Abracada Bati active
  const getAbracadaSection = () => {
    const path = location.pathname;
    if (path.includes('/artisans') || path.includes('/boutique')) return 'artisans';
    if (path.includes('/marketplace') || path.includes('/listings')) return 'annonces';
    if (path.includes('/reseau')) return 'communaute';
    if (path.includes('type=travaux')) return 'travaux';
    if (path.includes('type=depannage')) return 'depannage';
    return 'communaute';
  };

  const abracadaSection = getAbracadaSection();

  // Placeholder de recherche selon la section
  const getSearchPlaceholder = () => {
    switch (abracadaSection) {
      case 'artisans': return 'Rechercher un artisan...';
      case 'annonces': return 'Rechercher une annonce...';
      case 'communaute': return 'Rechercher un membre...';
      case 'travaux': return 'Rechercher des travaux...';
      case 'depannage': return 'Rechercher un depannage...';
      default: return 'Quel artisan recherchez-vous ?';
    }
  };

  // Pages qui ont leur propre header (masquer le header global)
  const pagesWithOwnHeader = [
    '/reseau',
    '/reseau/reels', 
    '/reseau/decouvrir',
    '/reseau/emploi',
    '/reseau/evenements',
  ];
  
  // Vérifier si on est sur une page avec son propre header
  const hasOwnHeader = pagesWithOwnHeader.some(p => location.pathname === p);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header AbracadaWorld Global - Masqué sur les pages avec leur propre header */}
      {!hasOwnHeader && (
        <>
          <div className="fixed top-0 left-0 right-0 z-50">
            {/* Barre principale */}
            <header className="bg-[#1E40AF] text-white">
              <div className="max-w-7xl mx-auto px-4">
                <div className="flex items-center justify-between h-14">
                  {/* Logo */}
                  <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="header-logo">
                    <Building2 size={24} className="text-[#F97316]" />
                    <span className="font-bold text-lg md:text-xl text-white">
                      Abracada Bati
                    </span>
                  </Link>
              
              {/* Actions droite - Desktop */}
              <div className="hidden md:flex items-center gap-2">
                {profile ? (
                  <>
                    <button className="p-2 hover:bg-white/10 rounded-full transition-colors relative">
                      <Bell size={20} />
                      {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
                          {unreadCount}
                        </span>
                      )}
                    </button>
                    <Link to="/reseau/messages" className="p-2 hover:bg-white/10 rounded-full transition-colors">
                      <MessageCircle size={20} />
                    </Link>
                    <Link to={`/reseau/profil/${profile.username}`} className="flex items-center gap-2 px-3 py-1.5 hover:bg-white/10 rounded-full transition-colors">
                      <div className="w-7 h-7 rounded-full bg-white/20 overflow-hidden">
                        {profile.avatar_url ? (
                          <img src={profile.avatar_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <User size={16} className="w-full h-full p-1" />
                        )}
                      </div>
                      <span className="text-sm font-medium hidden lg:block">@{profile.username}</span>
                    </Link>
                  </>
                ) : (
                  <>
                    <Link to="/reseau/connexion">
                      <Button variant="outline" size="sm" className="border-white text-white hover:bg-white hover:text-[#1E40AF]">
                        Connexion
                      </Button>
                    </Link>
                  </>
                )}
                <Link to="/batiassist">
                  <Button size="sm" className="bg-[#F97316] text-white hover:bg-[#EA580C]">
                    Espace Pro
                  </Button>
                </Link>
              </div>

              {/* Actions Mobile - Messages, Notifications, Menu */}
              <div className="flex md:hidden items-center gap-1">
                <Link to="/reseau/messages" className="p-2 hover:bg-white/10 rounded-full transition-colors relative">
                  <MessageCircle size={20} />
                </Link>
                <Link to="/reseau/notifications" className="p-2 hover:bg-white/10 rounded-full transition-colors relative">
                  <Bell size={20} />
                  {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full"></span>
                  )}
                </Link>
                <button 
                  className="p-2 hover:bg-white/10 rounded-lg"
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                >
                  {showMobileMenu ? <X size={24} /> : <Menu size={24} />}
                </button>
              </div>
            </div>
          </div>
          
          {/* Navigation tabs AbracadaWorld - Plus compact */}
          <div className="bg-[#1E40AF]/95">
            <div className="max-w-7xl mx-auto px-3 py-2">
              <nav className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
                {ABRACADA_SECTIONS.map((section) => {
                  const Icon = section.icon;
                  const isActiveSection = abracadaSection === section.id;
                  return (
                    <Link
                      key={section.id}
                      to={section.path}
                      className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full transition-all whitespace-nowrap text-xs font-medium ${
                        isActiveSection 
                          ? 'bg-white text-[#1E40AF]' 
                          : 'bg-white/10 hover:bg-white/20 text-white'
                      }`}
                    >
                      <Icon size={14} />
                      <span>{section.label}</span>
                    </Link>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Barre de recherche compacte - Masquee sur les pages avec leur propre recherche */}
          {!location.pathname.includes('/artisans') && !location.pathname.includes('/marketplace') && (
            <div className="bg-[#F97316] py-2 px-3">
              <div className="max-w-3xl mx-auto">
                <div className="flex items-center gap-2 bg-white rounded-lg overflow-hidden">
                  <div className="flex-1 flex items-center gap-2 px-3 py-2">
                    <Search size={18} className="text-gray-400 flex-shrink-0" />
                    <input
                      type="text"
                      placeholder={getSearchPlaceholder()}
                      className="flex-1 outline-none text-gray-900 placeholder-gray-400 bg-transparent text-sm"
                    />
                  </div>
                  <div className="hidden sm:flex items-center gap-2 px-3 py-2 border-l border-gray-200">
                    <MapPin size={16} className="text-gray-400" />
                    <input
                      type="text"
                      placeholder="Où ?"
                      className="w-24 outline-none text-gray-900 placeholder-gray-400 bg-transparent text-sm"
                    />
                  </div>
                  <button className="bg-[#1E40AF] hover:bg-[#1E3A8A] text-white px-4 py-2 transition-colors">
                    <Search size={18} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </header>
      </div>

      {/* Mobile Menu AbracadaWorld */}
      <AnimatePresence>
        {showMobileMenu && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 top-14 z-40 bg-white md:hidden"
          >
            <nav className="p-4 space-y-2">
              {profile ? (
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl mb-4">
                  <div className="w-12 h-12 rounded-full bg-blue-100 overflow-hidden">
                    {profile.avatar_url ? (
                      <img src={profile.avatar_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <User size={24} className="w-full h-full p-3 text-blue-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{profile.display_name || profile.username}</p>
                    <p className="text-sm text-gray-500">@{profile.username}</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-2 mb-4">
                  <Link to="/reseau/connexion" onClick={() => setShowMobileMenu(false)}>
                    <Button variant="outline" className="w-full">Connexion</Button>
                  </Link>
                  <Link to="/reseau/inscription" onClick={() => setShowMobileMenu(false)}>
                    <Button className="w-full bg-[#1E40AF]">S'inscrire</Button>
                  </Link>
                </div>
              )}
              
              <p className="text-xs font-semibold text-gray-500 uppercase px-2 pt-4">Sections</p>
              {ABRACADA_SECTIONS.map((section) => {
                const Icon = section.icon;
                return (
                  <Link
                    key={section.id}
                    to={section.path}
                    onClick={() => setShowMobileMenu(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      abracadaSection === section.id
                        ? 'bg-blue-50 text-[#1E40AF]'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Icon size={22} />
                    <span className="font-medium">{section.label}</span>
                  </Link>
                );
              })}
              
              <div className="pt-4 border-t border-gray-200 mt-4">
                <Link to="/batiassist" onClick={() => setShowMobileMenu(false)}>
                  <Button className="w-full bg-[#F97316] hover:bg-[#EA580C]">
                    Espace Pro
                  </Button>
                </Link>
              </div>

              {profile && (
                <button
                  onClick={() => { handleLogout(); setShowMobileMenu(false); }}
                  className="flex items-center gap-3 px-4 py-3 text-red-600 w-full mt-4"
                >
                  <LogOut size={22} />
                  <span className="font-medium">Déconnexion</span>
                </button>
              )}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
        </>
      )}

      {/* Main Content - padding ajuste selon presence de la barre de recherche */}
      <main className={`min-h-screen pb-20 md:pb-0 ${
        hasOwnHeader 
          ? 'pt-0' 
          : location.pathname.includes('/artisans') || location.pathname.includes('/marketplace') 
            ? 'pt-24 sm:pt-20' 
            : 'pt-32 sm:pt-28'
      }`}>
        <Outlet context={{ user, profile, setProfile }} />
      </main>
      
      {/* Menu complet du réseau social (slide from right) */}
      <SocialMenuPanel 
        isOpen={showSocialMenu}
        onClose={() => setShowSocialMenu(false)}
        profile={profile}
        onLogout={handleLogout}
      />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// MENU COMPLET DU RÉSEAU SOCIAL (slide from right)
// ══════════════════════════════════════════════════════════════════
function SocialMenuPanel({ isOpen, onClose, profile, onLogout }) {
  const navigate = useNavigate();
  
  const menuSections = [
    {
      title: 'Mon compte',
      items: [
        { icon: User, label: 'Mon profil', path: profile ? `/reseau/profil/${profile.username}` : '/reseau/connexion', color: '#3B82F6' },
        { icon: Bookmark, label: 'Enregistrements', path: '/reseau/enregistrements', color: '#8B5CF6' },
        { icon: Heart, label: 'Mes likes', path: '/reseau/likes', color: '#EF4444' },
        { icon: MessageCircle, label: 'Messages', path: '/reseau/messages', color: '#10B981' },
        { icon: Bell, label: 'Notifications', path: '/reseau/notifications', color: '#F59E0B' },
      ]
    },
    {
      title: 'Découvrir',
      items: [
        { icon: Map, label: 'Chantiers près de chez toi', path: '/reseau/chantiers', color: '#EF4444' },
        { icon: Briefcase, label: 'Emploi', path: '/reseau/emploi', color: '#2563EB' },
        { icon: Calendar, label: 'Événements', path: '/reseau/evenements', color: '#7C3AED' },
        { icon: Store, label: 'Artisans', path: '/artisans', color: '#F97316' },
        { icon: Package, label: 'Marketplace', path: '/produits', color: '#3B82F6' },
        { icon: ShoppingBag, label: 'Petites annonces', path: '/annonces', color: '#22C55E' },
        { icon: Users, label: 'Salons thématiques', path: '/reseau/salons', color: '#8B5CF6' },
      ]
    },
    {
      title: 'Créer',
      items: [
        { icon: Camera, label: 'Nouvelle publication', path: '/reseau/publier', color: '#F97316' },
        { icon: Play, label: 'Nouveau Reel', path: '/reseau/publier?type=reel', color: '#EC4899' },
        { icon: ShoppingBag, label: 'Déposer une annonce', path: '/marketplace/deposer', color: '#22C55E' },
      ]
    },
    {
      title: 'Paramètres',
      items: [
        { icon: Settings, label: 'Paramètres', path: '/reseau/parametres', color: '#6B7280' },
        { icon: Shield, label: 'Confidentialité', path: '/reseau/confidentialite', color: '#3B82F6' },
        { icon: HelpCircle, label: 'Aide', path: '/aide', color: '#10B981' },
      ]
    },
  ];
  
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={onClose}
          />
          
          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed top-0 right-0 bottom-0 w-[85%] max-w-sm bg-white z-50 flex flex-col overflow-hidden"
          >
            {/* Header avec profil */}
            <div className="bg-gradient-to-r from-[#1E40AF] to-[#2563EB] px-4 py-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-white font-bold text-lg">Menu</h2>
                <button 
                  onClick={onClose}
                  className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center"
                >
                  <X size={18} className="text-white" />
                </button>
              </div>
              
              {profile ? (
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-white/20 overflow-hidden ring-2 ring-white/50">
                    {profile.profile_photo ? (
                      <img src={profile.profile_photo} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User size={28} className="text-white" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-white">{profile.display_name || profile.username}</p>
                    <p className="text-white/70 text-sm">@{profile.username}</p>
                    {profile.user_type === 'professionnel' && (
                      <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-[#F97316] rounded-full text-[10px] font-semibold text-white">
                        <Star size={10} />
                        PRO
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <button
                    onClick={() => { navigate('/reseau/connexion'); onClose(); }}
                    className="w-full py-2.5 bg-white text-[#1E40AF] rounded-xl font-semibold text-sm"
                  >
                    Se connecter
                  </button>
                  <button
                    onClick={() => { navigate('/reseau/inscription'); onClose(); }}
                    className="w-full py-2.5 bg-white/20 text-white rounded-xl font-medium text-sm flex items-center justify-center gap-2"
                  >
                    <UserPlus size={16} />
                    Créer un compte
                  </button>
                </div>
              )}
            </div>
            
            {/* Contenu du menu scrollable */}
            <div className="flex-1 overflow-y-auto">
              {menuSections.map((section, sIdx) => (
                <div key={sIdx} className="border-b border-gray-100">
                  <h3 className="px-4 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wide bg-gray-50">
                    {section.title}
                  </h3>
                  <div className="py-1">
                    {section.items.map((item, iIdx) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={iIdx}
                          onClick={() => { navigate(item.path); onClose(); }}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                          data-testid={`menu-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <div 
                            className="w-10 h-10 rounded-xl flex items-center justify-center"
                            style={{ backgroundColor: `${item.color}15` }}
                          >
                            <Icon size={20} style={{ color: item.color }} />
                          </div>
                          <span className="flex-1 text-left font-medium text-gray-700">{item.label}</span>
                          <ChevronRight size={16} className="text-gray-400" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              
              {/* Espace Pro */}
              <div className="p-4">
                <button
                  onClick={() => { navigate('/batiassist'); onClose(); }}
                  className="w-full py-3 bg-gradient-to-r from-[#F97316] to-[#EA580C] text-white rounded-xl font-semibold flex items-center justify-center gap-2"
                >
                  <Briefcase size={18} />
                  Accéder à l'Espace Pro
                </button>
              </div>
            </div>
            
            {/* Footer avec déconnexion */}
            {profile && (
              <div className="border-t border-gray-200 p-4">
                <button
                  onClick={() => { onLogout(); onClose(); }}
                  className="w-full flex items-center justify-center gap-2 py-2.5 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                >
                  <LogOut size={18} />
                  <span className="font-medium">Déconnexion</span>
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
